import { useEffect } from "react";
import { NavLink } from "react-router-dom";
import {
  LayoutDashboard, KeyRound, Database, ArrowRightLeft, FileText,
  Shield, FlaskConical, MessageSquare, ClipboardList, BarChart3, Settings,
  ChevronLeft, ChevronRight, GitBranch, Building2, X, Users, UsersRound, FolderSync, UploadCloud,
  ShieldCheck,
  Gauge,
} from "lucide-react";
import { useUIStore } from "@/store/uiStore";
import { useAuthStore } from "@/store/authStore";
import { useIsDesktop } from "@/hooks/useMediaQuery";
import { cn } from "@/lib/utils";
import { PlanBadge } from "@/components/billing/PlanBadge";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useIsTruncated } from "@/hooks/useIsTruncated";
import { normalizePlanTier } from "@/types/billing";

const NAV_SECTIONS = [
  {
    label: "Observe",
    items: [
      { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
      { to: "/secured-retrievals", label: "Secured Retrievals", icon: ShieldCheck },
      { to: "/audit-log", label: "Audit Logs", icon: ClipboardList },
      { to: "/answer-synthesis", label: "Answer Synthesis", icon: MessageSquare },
      { to: "/performance-self-test", label: "Performance Self-Test", icon: Gauge },
    ],
  },
  {
    label: "Connect",
    items: [
      { to: "/identity-providers", label: "Identity Providers", icon: KeyRound },
      { to: "/directory", label: "Directory", icon: UsersRound },
      { to: "/connectors", label: "Connectors", icon: Database },
      { to: "/sources", label: "Sources", icon: FolderSync },
      { to: "/pipelines", label: "Pipelines", icon: ArrowRightLeft },
      { to: "/ingestion", label: "Ingestion", icon: UploadCloud },
      { to: "/data-catalog", label: "Data Catalog", icon: FileText },
      { to: "/settings/api-keys", label: "API Keys", icon: KeyRound },
    ],
  },
  {
    label: "Govern",
    items: [
      { to: "/policy-studio", label: "Policy Studio", icon: Shield },
      { to: "/access-simulator", label: "Access Simulator", icon: FlaskConical },
      { to: "/relationships", label: "Relationships", icon: GitBranch },
    ],
  },
  {
    label: "Billing",
    items: [
      { to: "/usage-billing", label: "Usage & Billing", icon: BarChart3 },
    ],
  },
];

// API Keys lives in the Connect group above (it is a setup step); listing it
// here as well showed the entry twice in the rail (cold run 2026-09-25, #16).
const FOOTER_NAV = [
  { to: "/settings?tab=organization", label: "Organization", icon: Building2 },
  { to: "/settings", label: "Settings", icon: Settings },
];

// Shown only to platform admins (user.is_platform_admin).
const ADMIN_SECTION = {
  label: "Platform Admin",
  items: [
    { to: "/admin/organizations", label: "Organizations", icon: Building2 },
    { to: "/admin/users", label: "Users", icon: Users },
  ],
};


interface NavItemProps {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  /** When true the label is shown; when false the item collapses to an icon rail. */
  expanded: boolean;
  onNavigate?: () => void;
}

function NavItem({ to, label, icon: Icon, expanded, onNavigate }: NavItemProps) {
  const { ref: labelRef, isTruncated } = useIsTruncated<HTMLSpanElement>();

  const item = (
    <NavLink
      to={to}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          "relative flex items-center gap-3 px-3 py-2 text-sm rounded-md transition-colors",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1 focus-visible:ring-offset-slate-900",
          isActive
            ? "bg-blue-500/15 text-blue-400 font-medium before:absolute before:left-0 before:top-2 before:bottom-2 before:w-0.5 before:bg-blue-400 before:rounded-r"
            : "text-slate-400 hover:bg-slate-800 hover:text-white",
          !expanded && "justify-center px-0 py-2.5"
        )
      }
    >
      <Icon className="h-4 w-4 shrink-0" />
      {expanded && (
        <span ref={labelRef} className="truncate">
          {label}
        </span>
      )}
    </NavLink>
  );

  // Collapsed: the label is gone entirely. Expanded: only when `truncate` clipped it.
  // Previously this was collapsed-only, and `expanded` is forced true below 1024px,
  // so no sidebar tooltip ever rendered on a phone or tablet.
  if (!expanded || isTruncated) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{item}</TooltipTrigger>
        <TooltipContent side="right">{label}</TooltipContent>
      </Tooltip>
    );
  }

  return item;
}

export function Sidebar() {
  const { sidebarOpen, setSidebarOpen, mobileNavOpen, setMobileNavOpen } = useUIStore();
  const user = useAuthStore((s) => s.user);
  const isDesktop = useIsDesktop();

  // On mobile the drawer always shows full labels; the icon-rail is desktop-only.
  const expanded = isDesktop ? sidebarOpen : true;
  const closeMobileNav = () => setMobileNavOpen(false);

  // Collapse the drawer automatically when growing to desktop, and lock body
  // scroll while the mobile drawer is open.
  useEffect(() => {
    if (isDesktop) setMobileNavOpen(false);
  }, [isDesktop, setMobileNavOpen]);

  useEffect(() => {
    if (mobileNavOpen && !isDesktop) {
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = "";
      };
    }
  }, [mobileNavOpen, isDesktop]);

  return (
    <>
      {/* Backdrop — mobile only, closes the drawer on tap */}
      {mobileNavOpen && (
        <div
          aria-hidden
          onClick={closeMobileNav}
          className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm lg:hidden"
        />
      )}

      <aside
        className={cn(
          "fixed left-0 top-0 h-full w-64 bg-slate-900 border-r border-slate-700/50 flex flex-col",
          "z-50 lg:z-30 transition-all duration-200",
          // Mobile: slide in/out as a drawer. Desktop: always docked.
          mobileNavOpen ? "translate-x-0" : "-translate-x-full",
          "lg:translate-x-0",
          // Desktop width follows the collapse toggle; mobile is always full width.
          sidebarOpen ? "lg:w-64" : "lg:w-16"
        )}
      >
        {/* Logo + collapse/close button */}
        <div className="flex items-center h-20 border-b border-slate-700/50 px-4 gap-2">
          {expanded ? (
            <div className="flex flex-1 items-center gap-2 min-w-0">
              <img src="/logo-dark.png" alt="Gateco" className="h-14 w-auto shrink-0" />
              <PlanBadge tier={normalizePlanTier(user?.organization?.plan || 'free')} className="text-xs ml-auto" />
            </div>
          ) : (
            <div className="mx-auto flex items-center justify-center w-8 h-8 rounded-lg bg-blue-600">
              <span className="text-white font-bold text-sm">G</span>
            </div>
          )}
          {/* Desktop: collapse to rail */}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className={cn(
              "hidden lg:inline-flex shrink-0 p-1 rounded-md text-slate-400 hover:bg-slate-800 hover:text-white transition-colors",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
            )}
            aria-label={sidebarOpen ? "Collapse sidebar" : "Expand sidebar"}
          >
            {sidebarOpen ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </button>
          {/* Mobile: close drawer */}
          <button
            onClick={closeMobileNav}
            className={cn(
              "inline-flex lg:hidden shrink-0 p-1 rounded-md text-slate-400 hover:bg-slate-800 hover:text-white transition-colors",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
            )}
            aria-label="Close navigation"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Org name */}
        {expanded && user?.organization && (
          <div className="px-4 py-2.5 border-b border-slate-700/50">
            <p className="text-xs text-slate-400 truncate">{user.organization.name}</p>
          </div>
        )}

        {/* Main nav */}
        <nav className="flex-1 py-3 overflow-y-auto overflow-x-hidden px-2 space-y-4">
          {(user?.is_platform_admin ? [...NAV_SECTIONS, ADMIN_SECTION] : NAV_SECTIONS).map((section) => (
            <div key={section.label}>
              {expanded && (
                <p className="px-3 mb-1 text-[10px] font-semibold uppercase tracking-widest text-slate-500">
                  {section.label}
                </p>
              )}
              <div className="space-y-0.5">
                {section.items.map((item) => (
                  <NavItem key={item.to} {...item} expanded={expanded} onNavigate={closeMobileNav} />
                ))}
              </div>
            </div>
          ))}
        </nav>

        {/* Footer nav */}
        <div className="border-t border-slate-700/50 py-2 px-2 space-y-0.5">
          {FOOTER_NAV.map((item) => (
            <NavItem key={item.to} {...item} expanded={expanded} onNavigate={closeMobileNav} />
          ))}
        </div>
      </aside>
    </>
  );
}
